/**
 * One read, two digests (DI-2-1 §2.4).
 *
 *   md5Hex            — MD5 over the whole file: the upload manifest /
 *                       `Content-MD5` hash and the legacy `image_checksum_md5`
 *                       (`01` §6.6). MD5 is not in WebCrypto, hence `spark-md5`.
 *   payloadSha256Hex  — SHA-256 from the first SOS to the end: the duplicate
 *                       key that survives a metadata rewrite (DI-D4 c1).
 *
 * The file is read ONCE into an ArrayBuffer (≤ 13 MB per Sony file; one file
 * in flight per worker lane, so memory stays flat) and both digests run over
 * that buffer. Nothing here is written back (DI-D11 — the original is what is
 * hashed).
 */

import SparkMD5 from "spark-md5";
import type { HashResult } from "./types.js";
import { scanStart, sha256Hex } from "./jpeg/hash.js";

export function md5Hex(bytes: Uint8Array): string {
  const ab =
    bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
      ? bytes.buffer
      : bytes.slice().buffer;
  return SparkMD5.ArrayBuffer.hash(ab);
}

/** The incremental form — used where a file is fed in chunks. */
export function md5HexChunked(chunks: readonly Uint8Array[]): string {
  const spark = new SparkMD5.ArrayBuffer();
  for (const c of chunks) spark.append(c.slice().buffer);
  return spark.end();
}

export async function hashBytes(bytes: Uint8Array): Promise<HashResult> {
  const start = scanStart(bytes);
  return {
    md5Hex: md5Hex(bytes),
    payloadSha256Hex:
      start === null ? null : await sha256Hex(bytes.subarray(start)),
    scanStart: start,
  };
}

/** Reads the whole file once and returns both digests. */
export async function hashFile(file: Blob): Promise<HashResult> {
  return hashBytes(new Uint8Array(await file.arrayBuffer()));
}
